/*
  imh-flex-grid.js — Fleksibel justert rad-layout for Ida Monrad Haugen.

  - Bredden settes på <a class="imh-card">, radhøyden på BILDEBOKSEN
    (.imh-card__media). Teksten under har fast høyde (data-caption-height),
    slik at bilderytmen blir jevn og Big Edition kan regnes kant-i-kant.
  - Kortene bruker hvert bildes FAKTISKE forhold (data-ratio) → ingen
    beskjæring, ingen hvit luft.
  - Konfigurerbart: mål-radhøyde, avstand mellom bilder (gh) og rader (gv),
    maks antall per rad, samt Big Edition av/på og brekkpunkt.
*/
(function () {
  'use strict';

  function cfg(root, name, fallback) {
    var v = parseFloat(root.getAttribute(name));
    return isNaN(v) ? fallback : v;
  }

  function ratioOf(card) {
    var r = parseFloat(card.getAttribute('data-ratio'));
    if (isNaN(r) || r <= 0) return 1;
    return r;
  }

  function sumRatios(cards) {
    var s = 0;
    for (var i = 0; i < cards.length; i++) s += ratioOf(cards[i]);
    return s;
  }

  // Stokker en liste tilfeldig (Fisher-Yates). Brukes til «smakebit»-
  // utvalget i Nye arbeider, så forsiden roterer ved hver lasting.
  function shuffle(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = arr[i]; arr[i] = arr[j]; arr[j] = tmp;
    }
    return arr;
  }

  // Teller hvor mange små (ikke-høye) arbeider som følger fra og med `start`,
  // fram til neste høye arbeid. Brukes for å avgjøre om et høyt arbeid har
  // nok små ved siden til å fortjene dobbel høyde.
  function countLeadingNormals(arr, start) {
    var n = 0;
    for (var i = start; i < arr.length; i++) {
      if (arr[i].getAttribute('data-big') === 'true') break;
      n++;
    }
    return n;
  }

  function bestSplit(cards, maxLen) {
    var best = null;
    var bestDiff = Infinity;
    for (var i = 1; i < cards.length; i++) {
      var a = cards.slice(0, i);
      var b = cards.slice(i);
      if (maxLen && (a.length > maxLen || b.length > maxLen)) continue;
      var d = Math.abs(sumRatios(a) - sumRatios(b));
      if (d < bestDiff) { bestDiff = d; best = { a: a, b: b }; }
    }
    if (!best) {
      var mid = Math.ceil(cards.length / 2);
      best = { a: cards.slice(0, mid), b: cards.slice(mid) };
    }
    return best;
  }

  function clearLayout(card) {
    card.style.width = '';
    var media = card.querySelector('.imh-card__media');
    if (media) media.style.height = '';
  }

  // Omrokkerer kortene for tettest mulig pakking: sprer høye (dobbel-rad)
  // arbeider utover med nok små ved siden av hver, så to høye ikke havner
  // rett etter hverandre. Brukes kun når "best fit"-overstyring er på.
  function bestFitOrder(cards, maxPerRow, allowBig) {
    var bigs = [];
    var normals = [];
    for (var i = 0; i < cards.length; i++) {
      if (allowBig && cards[i].getAttribute('data-big') === 'true') bigs.push(cards[i]);
      else normals.push(cards[i]);
    }
    if (!bigs.length) return cards.slice();

    var perBig = 2 * Math.max(1, maxPerRow - 1); // små som fyller én stors sider
    var per = Math.max(perBig, Math.floor(normals.length / bigs.length));
    var out = [];
    var ni = 0;
    for (var b = 0; b < bigs.length; b++) {
      out.push(bigs[b]);
      var count = (b === bigs.length - 1) ? (normals.length - ni) : per;
      for (var k = 0; k < count && ni < normals.length; k++) out.push(normals[ni++]);
    }
    while (ni < normals.length) out.push(normals[ni++]);
    return out;
  }

  // Mobil-variant av best-fit: liggende arbeider står alltid alene i bredden,
  // så her handler det bare om å pare stående med stående og kvadratiske med
  // kvadratiske. Rekkefølgen bevares så godt som mulig; et stående/kvadratisk
  // arbeider «venter» på neste av samme type og pares da opp. Eventuelle
  // enslige til overs havner til slutt.
  function mobileBestFitOrder(cards) {
    var out = [];
    var holdP = null; // ventende stående
    var holdS = null; // ventende kvadratisk
    for (var i = 0; i < cards.length; i++) {
      var r = ratioOf(cards[i]);
      if (r > 1.2) {
        out.push(cards[i]); // liggende: alltid alene
      } else if (r < 0.85) {
        if (holdP) { out.push(holdP, cards[i]); holdP = null; }
        else holdP = cards[i];
      } else {
        if (holdS) { out.push(holdS, cards[i]); holdS = null; }
        else holdS = cards[i];
      }
    }
    if (holdP) out.push(holdP);
    if (holdS) out.push(holdS);
    return out;
  }

  // Forkorter flapp-tekstene i prioritert rekkefølge til den samlede bredden
  // får plass i bildeboksen. Big Edition forkortes først, så Utsolgt, så
  // «X igjen»; «Ny» forkortes aldri. Kjøres per kort ved hver layout/resize.
  // Sentrerer tittelen under bildet når den får plass sentrert uten å
  // kollidere med den høyrestilte dimensjonen; ellers venstrestilles den
  // (og får reservert plass til dimensjonen). Kjøres per kort ved resize.
  function fitTitle(card) {
    var line = card.querySelector('.imh-card__titleline');
    if (!line) return;
    var titleEl = line.querySelector('.imh-card__title');
    if (!titleEl) return;
    var dimEl = line.querySelector('.imh-card__dim');
    var W = line.clientWidth;
    if (!W) return;
    var dimW = dimEl ? dimEl.offsetWidth : 0;
    var gap = 10;

    // Mål tittelens naturlige bredde på én linje. Som display:block ville
    // scrollWidth returnert full elementbredde, ikke tekstbredden — så vi
    // måler den midlertidig som inline-block (krymper til innholdet).
    titleEl.style.paddingRight = '0';
    var prevWS = titleEl.style.whiteSpace;
    var prevDisp = titleEl.style.display;
    titleEl.style.whiteSpace = 'nowrap';
    titleEl.style.display = 'inline-block';
    var titleW = titleEl.scrollWidth;
    titleEl.style.whiteSpace = prevWS;
    titleEl.style.display = prevDisp;

    if (titleW <= W - 2 * (dimW + gap)) {
      line.classList.remove('imh-card__titleline--left');
      titleEl.style.paddingRight = '';
    } else {
      line.classList.add('imh-card__titleline--left');
      titleEl.style.paddingRight = dimEl ? (dimW + gap) + 'px' : '';
    }
  }

  function fitBadges(card, indent) {
    var badgesEl = card.querySelector('.imh-card__badges');
    if (!badgesEl || !badgesEl.children.length) return;
    var media = card.querySelector('.imh-card__media');
    var boxW = media ? media.clientWidth : card.clientWidth;
    if (!boxW) return;
    var avail = boxW - indent;

    // Nullstill til full tekst før ny måling.
    var spans = badgesEl.children;
    for (var i = 0; i < spans.length; i++) {
      var full = spans[i].getAttribute('data-full');
      if (full !== null) spans[i].textContent = full;
    }

    var order = ['big', 'sold', 'few'];
    for (var k = 0; k < order.length; k++) {
      if (badgesEl.offsetWidth <= avail) break;
      var b = badgesEl.querySelector('.imh-badge--' + order[k]);
      if (b) {
        var short = b.getAttribute('data-short');
        if (short !== null) b.textContent = short;
      }
    }
  }

  // Avgjør hvilken side det store bildet i en dobbel-rad skal ligge på.
  // Teaser («Nye arbeider»): tilfeldig per lasting, men stabilt ved resize
  // (lagres på kortet). Ellers (samlingssider): fast per arbeid via produkt-ID,
  // så det aldri hopper — verken ved resize eller ny lasting.
  function bigOnRight(bigCard, isTeaser) {
    if (isTeaser) {
      if (bigCard._imhSide === undefined) bigCard._imhSide = Math.random() < 0.5;
      return bigCard._imhSide;
    }
    // Fast, godt fordelt side per arbeid: hash av produkt-URL-en (alltid til
    // stede). Unngår avhengighet av data-pid og av ID-ens paritet, som kan
    // være skjev.
    var key = bigCard.getAttribute('href') || bigCard.getAttribute('data-pid') || '';
    var h = 5381;
    for (var i = 0; i < key.length; i++) {
      h = ((h << 5) + h + key.charCodeAt(i)) >>> 0;
    }
    return ((h >>> 15) & 1) === 1;
  }

  function layout(root) {
    var itemsWrap = root.querySelector('[data-imh-items]');
    if (!itemsWrap) return;

    var W = itemsWrap.clientWidth;
    if (W <= 0) return;

    var gh = cfg(root, 'data-image-gap', 8);   // vannrett, mellom bilder
    var gv = cfg(root, 'data-row-gap', 20);    // loddrett, mellom rader
    var C = cfg(root, 'data-caption-height', 96); // fast teksthøyde
    var maxPerRow = Math.max(1, cfg(root, 'data-max-per-row', 4));
    var bigEnabled = root.getAttribute('data-big-enabled') !== 'false';
    var bigBreakpoint = cfg(root, 'data-big-breakpoint', 600);
    var bigMinSides = Math.max(2, cfg(root, 'data-big-min-sides', 2));
    var bestFit = root.getAttribute('data-best-fit') === 'true';
    var numRows = cfg(root, 'data-num-rows', 0); // 0 = av (vis alt)

    var isNarrow = W < bigBreakpoint;
    var stackBelowMin = root.getAttribute('data-stack-below-min') === 'true';

    if (isNarrow) maxPerRow = Math.min(maxPerRow, 2);
    var allowBig = bigEnabled && !isNarrow;

    // På mobil viser hver rad færre bilder (liggende = 1, stående/kv. = 2),
    // så vi dobler antall rader for å få tilsvarende mengde innhold.
    var effectiveRows = numRows;
    if (isNarrow && numRows > 0) effectiveRows = numRows * 2;

    var allCards = Array.prototype.slice.call(
      itemsWrap.querySelectorAll('[data-imh-card]:not(.imh-card--filtered)')
    );
    // Smakebit-modus: når vi allerede har trukket et utvalg, jobb bare med
    // de synlige kortene (ikke stokk om på nytt ved vindusendring).
    if (numRows > 0) {
      allCards = allCards.filter(function (c) { return !c.hasAttribute('data-imh-hidden'); });
    }
    if (!allCards.length) {
      // Vis «ingen treff»-melding hvis alle er filtrert bort
      var noHit = root.querySelector('.imh-no-results');
      if (noHit) noHit.style.display = 'block';
      return;
    }
    var noHit2 = root.querySelector('.imh-no-results');
    if (noHit2) noHit2.style.display = 'none';

    // Anbefalingsmodus: stable som mobil dersom kortene ellers ville blitt
    // smalere enn minstebredden fra IMH-innstillingene.
    if (stackBelowMin && !isNarrow) {
      var perRowEst = Math.min(maxPerRow, allCards.length);
      var estW = (W - (perRowEst - 1) * gh) / perRowEst;
      var minSideW = cfg(root, 'data-min-side-width', 190);
      if (estW < minSideW) {
        isNarrow = true;
        maxPerRow = Math.min(maxPerRow, 2);
        allowBig = false;
        if (numRows > 0) effectiveRows = numRows * 2;
      }
    }

    // Tilfeldig utvalg trekkes ÉN gang (ved lasting), ikke ved resize.
    var firstSelect = numRows > 0 && !root._imhTeaserSelected;
    if (firstSelect) shuffle(allCards);

    // Generisk engangs-shuffle (f.eks. stedsoversikten), stabil ved resize.
    if (root.getAttribute('data-shuffle') === 'true' && !root._imhShuffled) {
      shuffle(allCards);
      root._imhShuffled = true;
    }

    if (bestFit) {
      allCards = isNarrow
        ? mobileBestFitOrder(allCards)
        : bestFitOrder(allCards, maxPerRow, allowBig);
    } else if (isNarrow && stackBelowMin) {
      // Stablet anbefalingsrad: par stående med stående, kvadratiske med
      // kvadratiske, liggende alene — også uten best-fit.
      allCards = mobileBestFitOrder(allCards);
    }

    // Målhøyde utledes internt fra maks per rad og gjennomsnittsforholdet,
    // slik at radene sikter mot ~maxPerRow arbeider i jevn høyde. Ingen
    // manuell radhøyde-innstilling trengs.
    var avgRatio = sumRatios(allCards) / allCards.length || 1;
    var targetH = (W - (maxPerRow - 1) * gh) / (maxPerRow * avgRatio);
    targetH = Math.max(140, Math.min(520, targetH));

    itemsWrap.style.display = 'flex';
    itemsWrap.style.flexDirection = 'column';
    itemsWrap.style.gap = gv + 'px';

    // Flat ut gamle rad-containere
    var oldRows = itemsWrap.querySelectorAll('[data-imh-row]');
    for (var r = 0; r < oldRows.length; r++) {
      while (oldRows[r].firstChild) itemsWrap.appendChild(oldRows[r].firstChild);
    }
    for (var q = oldRows.length - 1; q >= 0; q--) {
      oldRows[q].parentNode.removeChild(oldRows[q]);
    }

    allCards.forEach(clearLayout);

    var rows = [];

    if (isNarrow) {
      // ---- MOBIL ----
      // Liggende format: ett i bredden (full bredde). Stående/kvadratisk:
      // to i bredden. Et stående/kvadratisk som blir til overs står i halv
      // bredde (venstrestilt) i stedet for å blåses opp til full bredde.
      var LAND = 1.2; // ratio > LAND regnes som liggende
      var mi = 0;
      while (mi < allCards.length) {
        var mc = allCards[mi];
        var mr = ratioOf(mc);
        if (mr > LAND) {
          rows.push({ type: 'std', cards: [mc], h: W / mr });
          mi += 1;
        } else {
          var mc2 = (mi + 1 < allCards.length) ? allCards[mi + 1] : null;
          if (mc2 && ratioOf(mc2) <= LAND) {
            var mh = (W - gh) / (mr + ratioOf(mc2));
            rows.push({ type: 'std', cards: [mc, mc2], h: mh });
            mi += 2;
          } else {
            var halfW = (W - gh) / 2;
            rows.push({ type: 'std', cards: [mc], h: halfW / mr });
            mi += 1;
          }
        }
      }
    } else {
      // ---- DESKTOP ----
      var queue = allCards.slice();
      var buffer = [];
      var carry = []; // kort som er utsatt fordi de ellers ville stått alene

      var flushBuffer = function (isLast) {
        var all = carry.concat(buffer);
        carry = [];
        buffer = [];
        if (!all.length) return;

        var built = []; // ferdige rader (lister av kort), fyller alltid bredden
        var row = [];
        var rSum = 0;
        for (var i = 0; i < all.length; i++) {
          row.push(all[i]);
          rSum += ratioOf(all[i]);
          var filled = rSum * targetH + (row.length - 1) * gh >= W;
          var maxed = row.length >= maxPerRow;
          if (filled || maxed) {
            var remaining = all.length - 1 - i;
            // Unngå å etterlate nøyaktig ETT kort (som ville stått alene på
            // en rad): flytt heller et kort ned til neste rad, eller la det
            // siste kortet bli med i denne raden.
            if (remaining === 1 && maxPerRow >= 3) {
              if (row.length >= 3) {
                var moved = row.pop();
                built.push(row);
                row = [moved];
                rSum = ratioOf(moved);
                continue;
              } else {
                continue; // rad på 2: absorber det siste kortet i denne raden
              }
            }
            built.push(row);
            row = [];
            rSum = 0;
          }
        }
        var leftover = row; // 0, ≥2, eller (sjelden) 1

        // Ett enslig kort igjen: utsett det og prøv å kombinere det med kort
        // senere i griden i stedet for å la det stå alene.
        if (leftover.length === 1 && !isLast) {
          carry = leftover;
          leftover = [];
        }

        for (var b = 0; b < built.length; b++) {
          var cs = built[b];
          rows.push({ type: 'std', cards: cs, h: (W - (cs.length - 1) * gh) / sumRatios(cs) });
        }

        if (leftover.length) {
          var lh = (W - (leftover.length - 1) * gh) / sumRatios(leftover);
          // Bare et ENSLIG siste kort får stå smått (med tomrom til høyre).
          // Rader med to eller flere fyller alltid hele bredden.
          if (leftover.length === 1 && lh > targetH * 1.4) lh = targetH;
          rows.push({ type: 'std', cards: leftover, h: lh });
        }
      };

      // Minste akseptable bredde på et sidebilde i en dobbel-rad (fra
      // innstillingene). Blir de smalere (typisk når et bredt liggende
      // storbilde spiser plassen), demoteres storbildet til et vanlig kort.
      var MIN_SIDE_W = cfg(root, 'data-min-side-width', 190);

      while (queue.length) {
        // Dobbel høyde kun når et høyt arbeid har nok små ved siden til å
        // bygge en ordentlig kolonne. Ellers behandles det som et vanlig kort
        // og flyter inn i en justert rad (kan da dele rad med andre høye).
        if (allowBig
            && queue[0].getAttribute('data-big') === 'true'
            && countLeadingNormals(queue, 1) >= bigMinSides) {
          var big = queue[0];
          var bigR = ratioOf(big);

          // Det store bildet opptar én horisontal plass, så hver underrad
          // ved siden får maks (maxPerRow - 1) små kort. Vi ser framover og
          // samler side-kandidatene UTEN å ta dem av køen ennå.
          var maxSide = Math.max(1, maxPerRow - 1);
          var sideTarget = (2 * Math.max(1, W - 2 * bigR * targetH - gh)) / targetH;
          var sideCount = 0;
          var sSum = 0;
          for (var si = 1; si < queue.length && sideCount < 2 * maxSide; si++) {
            if (queue[si].getAttribute('data-big') === 'true') break;
            if (sideCount >= 2 && sSum >= sideTarget * 0.95) break;
            sSum += ratioOf(queue[si]);
            sideCount++;
          }
          var side = queue.slice(1, 1 + sideCount);

          var split = bestSplit(side, maxSide);
          var sum1 = sumRatios(split.a);
          var sum2 = sumRatios(split.b);
          var n1 = split.a.length;
          var n2 = split.b.length;
          var f = 1 / sum1;
          var g = 1 / sum2;
          // Big-bildet regnes så BUNNEN (bilde + fast tekst) møter den lille
          // kolonnen (to bilder + to tekstblokker + loddrett mellomrom).
          // Hb = h1 + h2 + C + gv  (utledet), med gh vannrett.
          var Hb = ((W - gh) * (f + g) - gh * ((n1 - 1) * f + (n2 - 1) * g) + C + gv) / (1 + bigR * (f + g));
          var Wb = bigR * Hb;
          var Ws = W - gh - Wb;
          var h1 = (Ws - (n1 - 1) * gh) / sum1;
          var h2 = (Ws - (n2 - 1) * gh) / sum2;

          // Hvor smalt blir det smaleste sidebildet?
          var minSideW = Infinity;
          for (var q1 = 0; q1 < split.a.length; q1++) minSideW = Math.min(minSideW, ratioOf(split.a[q1]) * h1);
          for (var q2 = 0; q2 < split.b.length; q2++) minSideW = Math.min(minSideW, ratioOf(split.b[q2]) * h2);

          if (minSideW >= MIN_SIDE_W) {
            // Dobbel-rad: fjern big + sidebilder fra køen og legg bandet inn.
            flushBuffer(false); // plasser ventende std-kort først
            queue.splice(0, 1 + sideCount);
            rows.push({ type: 'big-split', big: big, Hb: Hb, Wb: Wb, Ws: Ws, sub1: split.a, sub2: split.b, h1: h1, h2: h2 });
          } else {
            // Sidebildene ville blitt for små: demoter storbildet til et
            // vanlig bredt kort. Sidebildene blir liggende i køen og flyter
            // inn som vanlige kort etterpå.
            buffer.push(queue.shift());
          }
        } else {
          // Samle opp; tøm først ved en dobbel-rad eller på slutten, slik at
          // look-ahead kan se alle kortene og aldri etterlate ett alene.
          buffer.push(queue.shift());
        }
      }
      flushBuffer(true);
    }

    function makeRow(dir) {
      var el = document.createElement('div');
      el.setAttribute('data-imh-row', '');
      el.style.display = 'flex';
      el.style.flexDirection = dir || 'row';
      el.style.gap = (dir === 'column' ? gv : gh) + 'px';
      el.style.alignItems = 'flex-start';
      return el;
    }

    function placeCard(card, w, h) {
      card.style.width = w + 'px';
      var media = card.querySelector('.imh-card__media');
      if (media) media.style.height = h + 'px';
    }

    // Smakebit-utvalg: behold nøyaktig N rad-høyder (std = 1, dobbel = 2),
    // skjul resten. Gjøres bare ved første trekning (ikke ved resize).
    if (firstSelect) {
      var kept = [];
      var units = 0;
      var keepCards = [];
      for (var bi = 0; bi < rows.length; bi++) {
        var bandUnits = (rows[bi].type === 'std') ? 1 : 2;
        if (units + bandUnits <= effectiveRows) {
          kept.push(rows[bi]);
          units += bandUnits;
          if (rows[bi].type === 'std') {
            keepCards = keepCards.concat(rows[bi].cards);
          } else {
            keepCards = keepCards.concat([rows[bi].big], rows[bi].sub1, rows[bi].sub2);
          }
          if (units >= effectiveRows) break;
        }
        // ellers: hopp over (f.eks. en dobbel-rad som ville sprengt N) og se
        // om en senere std-rad kan fylle opp til nøyaktig N.
      }
      rows = kept;
      for (var m = 0; m < keepCards.length; m++) keepCards[m]._imhKeep = true;
      for (var a = 0; a < allCards.length; a++) {
        if (!allCards[a]._imhKeep) {
          allCards[a].setAttribute('data-imh-hidden', '');
          allCards[a].style.display = 'none';
        }
        delete allCards[a]._imhKeep;
      }
      root._imhTeaserSelected = true;
    }

    // Topp-padding bare HVIS øverste rad faktisk har en flapp. De øvrige
    // radenes flapper stjeler av rad-luften og trenger ingen ekstra plass.
    var topCards = [];
    if (rows.length) {
      if (rows[0].type === 'std') topCards = rows[0].cards;
      else topCards = [rows[0].big].concat(rows[0].sub1);
    }
    var topHasFlap = false;
    for (var ti = 0; ti < topCards.length; ti++) {
      var bd = topCards[ti].querySelector('.imh-card__badges');
      if (bd && bd.childElementCount > 0) { topHasFlap = true; break; }
    }
    itemsWrap.style.paddingTop = topHasFlap ? '' : '0px';

    var isTeaser = numRows > 0;
    rows.forEach(function (band) {
      var rowEl = makeRow('row');
      if (band.type === 'std') {
        band.cards.forEach(function (c) { placeCard(c, ratioOf(c) * band.h, band.h); rowEl.appendChild(c); });
      } else {
        var col = makeRow('column');
        col.style.width = band.Ws + 'px';
        var sub1 = makeRow('row'); sub1.style.width = band.Ws + 'px';
        band.sub1.forEach(function (c) { placeCard(c, ratioOf(c) * band.h1, band.h1); sub1.appendChild(c); });
        var sub2 = makeRow('row'); sub2.style.width = band.Ws + 'px';
        band.sub2.forEach(function (c) { placeCard(c, ratioOf(c) * band.h2, band.h2); sub2.appendChild(c); });
        col.appendChild(sub1);
        col.appendChild(sub2);
        placeCard(band.big, band.Wb, band.Hb);
        if (bigOnRight(band.big, isTeaser)) {
          rowEl.appendChild(col);
          rowEl.appendChild(band.big);
        } else {
          rowEl.appendChild(band.big);
          rowEl.appendChild(col);
        }
      }
      itemsWrap.appendChild(rowEl);
    });

    // Forkort flapp-tekstene der de ellers ville gått ut over bildeboksen.
    var flapIndent = parseFloat(getComputedStyle(itemsWrap).getPropertyValue('--imh-flap-indent')) || 14;
    var placedCards = itemsWrap.querySelectorAll('[data-imh-card]');
    for (var pc = 0; pc < placedCards.length; pc++) {
      if (placedCards[pc].hasAttribute('data-imh-hidden')) continue;
      fitBadges(placedCards[pc], flapIndent);
      fitTitle(placedCards[pc]);
    }

    // Klaring mot flapp: når en tittel har wrappet (pris havner på linje 3)
    // og raden under har en flapp, kan flappen komme borti prisen. Legg da
    // tilbake én tittellinjes «luft» under den raden. (Enkel variant — kan
    // gjøres horisontalt-presis senere om nødvendig.)
    var bands = itemsWrap.querySelectorAll(':scope > [data-imh-row]');
    for (var bi2 = 0; bi2 < bands.length - 1; bi2++) {
      var upper = bands[bi2];
      var upCards = upper.querySelectorAll('[data-imh-card]');
      var wrapped = false;
      var lh = 0;
      for (var u = 0; u < upCards.length; u++) {
        var te = upCards[u].querySelector('.imh-card__title');
        if (!te) continue;
        if (!lh) lh = parseFloat(getComputedStyle(te).lineHeight) || 0;
        if (lh && te.offsetHeight > lh * 1.5) { wrapped = true; break; }
      }
      if (!wrapped || !lh) continue;
      var lower = bands[bi2 + 1];
      var loCards = lower.querySelectorAll('[data-imh-card]');
      var hasFlap = false;
      for (var l = 0; l < loCards.length; l++) {
        var be = loCards[l].querySelector('.imh-card__badges');
        if (be && be.children.length) { hasFlap = true; break; }
      }
      if (hasFlap) upper.style.marginBottom = Math.round(lh) + 'px';
    }

    root.setAttribute('data-imh-ready', 'true');
  }

  function debounce(fn, wait) {
    var t;
    return function () { clearTimeout(t); t = setTimeout(fn, wait); };
  }

  function initAll() {
    var grids = document.querySelectorAll('.imh-flex-grid:not([data-imh-inited])');
    for (var i = 0; i < grids.length; i++) {
      (function (grid) {
        grid.setAttribute('data-imh-inited', '1');
        layout(grid);
        window.addEventListener('resize', debounce(function () { layout(grid); }, 150));

        grid.addEventListener('imh-relayout', function () { layout(grid); });

        var imgs = grid.querySelectorAll('img');
        var pending = imgs.length;
        if (pending > 0) {
          for (var j = 0; j < imgs.length; j++) {
            if (imgs[j].complete) { pending--; }
            else {
              imgs[j].addEventListener('load', function () { pending--; if (pending <= 0) layout(grid); });
              imgs[j].addEventListener('error', function () { pending--; if (pending <= 0) layout(grid); });
            }
          }
        }

        var toolbar = grid.parentElement
          ? grid.parentElement.querySelector('[data-imh-toolbar]')
          : null;
        if (toolbar) initToolbar(grid, toolbar);
      })(grids[i]);
    }
  }

  // ============================================================
  //  TOOLBAR — sortering, filtrering, chips
  // ============================================================

  var MONTHS = ['januar','februar','mars','april','mai','juni',
                'juli','august','september','oktober','november','desember'];

  function classifyTag(tag) {
    var t = tag.trim();
    if (!t) return null;
    var tn = t.toLowerCase().replace('×', 'x');
    if (/^\d+[.,]?\d*x\d+[.,]?\d*$/.test(tn)) return { type: 'format', value: t.replace(/\./g, ',') };
    if (/^\d{4}$/.test(t)) return { type: 'year', value: t };
    if (MONTHS.indexOf(t.toLowerCase()) !== -1) return null;
    if (t.toLowerCase() === 'big') return null;
    return { type: 'place', value: t };
  }

  function scanCards(cards, L) {
    if (!L) L = function(k, f) { return f; };
    var formats = {}, places = {}, years = {}, orientations = {}, prices = [];
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var tags = (card.getAttribute('data-tags') || '').split(',');
      for (var j = 0; j < tags.length; j++) {
        var c = classifyTag(tags[j]);
        if (!c) continue;
        if (c.type === 'format') formats[c.value] = (formats[c.value] || 0) + 1;
        else if (c.type === 'year') years[c.value] = (years[c.value] || 0) + 1;
        else if (c.type === 'place') places[c.value] = (places[c.value] || 0) + 1;
      }
      var ratio = parseFloat(card.getAttribute('data-ratio')) || 1;
      var orient = ratio > 1.15
        ? L('landscape', 'liggende')
        : ratio < 0.87
          ? L('portrait', 'stående')
          : L('square', 'kvadratisk');
      card._imhOrient = orient;
      orientations[orient] = (orientations[orient] || 0) + 1;
      var price = parseInt(card.getAttribute('data-price')) || 0;
      if (price > 0) prices.push(price);
    }
    return { formats: formats, places: places, years: years,
             orientations: orientations, prices: prices };
  }

  function makePriceGroups(prices) {
    if (!prices.length) return {};
    prices.sort(function (a, b) { return a - b; });
    var min = prices[0], max = prices[prices.length - 1];
    if (max - min < 500 || prices.length < 3) return {};

    // Kvantil-baserte kuttpunkter (tredeler av antall produkter)
    // slik at hver gruppe har omtrent like mange arbeider.
    var i1 = Math.floor(prices.length / 3);
    var i2 = Math.floor(2 * prices.length / 3);
    var cut1 = Math.round(prices[i1] / 500) * 500;
    var cut2 = Math.round(prices[i2] / 500) * 500;

    // Sikre at kuttene er ulike
    if (cut1 <= min) cut1 = min + 500;
    if (cut2 <= cut1) cut2 = cut1 + 500;
    if (cut2 >= max) cut2 = max;
    if (cut1 >= cut2) cut1 = cut2 - 500;

    var label1 = 'Under ' + cut1 + ' kr';
    var label2 = cut1 + ' – ' + cut2 + ' kr';
    var label3 = 'Over ' + cut2 + ' kr';
    var c1 = 0, c2 = 0, c3 = 0;
    for (var i = 0; i < prices.length; i++) {
      if (prices[i] < cut1) c1++;
      else if (prices[i] <= cut2) c2++;
      else c3++;
    }

    // Bygg kun grupper som har arbeider
    var groups = {};
    if (c1 > 0) groups[label1] = c1;
    if (c2 > 0) groups[label2] = c2;
    if (c3 > 0) groups[label3] = c3;

    if (Object.keys(groups).length < 2) return {};
    return { groups: groups, cut1: cut1, cut2: cut2 };
  }

  function buildDropdown(container, label, type, options, sortKeys) {
    if (!options || !Object.keys(options).length) return;
    // Bare vis dropdown hvis det er mer enn 1 valg
    if (Object.keys(options).length < 2 && type !== 'availability') return;

    var wrap = document.createElement('div');
    wrap.className = 'imh-filter';
    wrap.setAttribute('data-filter-type', type);

    var btn = document.createElement('button');
    btn.className = 'imh-filter__btn';
    btn.type = 'button';
    btn.textContent = label;
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      closeAllDropdowns(container, wrap);
      wrap.classList.toggle('imh-filter--open');
    });

    var dd = document.createElement('div');
    dd.className = 'imh-filter__dd';

    var keys = Object.keys(options);
    if (sortKeys) keys.sort(sortKeys);

    for (var i = 0; i < keys.length; i++) {
      var lbl = document.createElement('label');
      lbl.className = 'imh-filter__opt';
      var inp = document.createElement('input');
      inp.type = 'checkbox';
      inp.value = keys[i];
      inp.setAttribute('data-filter', type);
      lbl.appendChild(inp);
      lbl.appendChild(document.createTextNode(' ' + keys[i] + ' '));
      var cnt = document.createElement('span');
      cnt.className = 'imh-filter__count';
      cnt.textContent = '(' + options[keys[i]] + ')';
      lbl.appendChild(cnt);
      dd.appendChild(lbl);
    }

    wrap.appendChild(btn);
    wrap.appendChild(dd);
    container.appendChild(wrap);
  }

  function buildSortDropdown(container, currentSort, collUrl, L) {
    var wrap = document.createElement('div');
    wrap.className = 'imh-filter imh-filter--sort';

    var sel = document.createElement('select');
    sel.className = 'imh-sort__select';
    sel.setAttribute('data-imh-sort', '');
    sel.setAttribute('data-collection-url', collUrl);

    var opts = [
      { value: '',                    label: L('sort', 'Sortering'),                    server: true },
      { value: 'best-selling',        label: L('sort_best', 'Bestselgende'),             server: true },
      { value: 'created-descending',  label: L('sort_newest', 'Nyeste først'),            server: true },
      { value: 'created-ascending',   label: L('sort_oldest', 'Eldste først'),            server: true },
      { value: 'price-ascending',     label: L('sort_price_asc', 'Pris: lav → høy'),     server: true },
      { value: 'price-descending',    label: L('sort_price_desc', 'Pris: høy → lav'),    server: true },
      { value: 'qty-ascending',       label: L('sort_qty_asc', 'Færrest tilgj. først'),   server: false },
      { value: 'qty-descending',      label: L('sort_qty_desc', 'Flest tilgj. først'),    server: false }
    ];

    for (var i = 0; i < opts.length; i++) {
      var o = document.createElement('option');
      o.value = opts[i].value;
      o.textContent = opts[i].label;
      if (!opts[i].server) o.setAttribute('data-client', '');
      if (opts[i].value === currentSort) o.selected = true;
      sel.appendChild(o);
    }

    wrap.appendChild(sel);
    container.appendChild(wrap);
    return sel;
  }

  function buildAvailabilityToggle(container, count, L) {
    var wrap = document.createElement('label');
    wrap.className = 'imh-filter__toggle';
    var inp = document.createElement('input');
    inp.type = 'checkbox';
    inp.setAttribute('data-filter', 'availability');
    wrap.appendChild(inp);
    wrap.appendChild(document.createTextNode(' ' + L('availability', 'Kun tilgjengelige') + ' '));
    var cnt = document.createElement('span');
    cnt.className = 'imh-filter__count';
    cnt.textContent = '(' + count + ')';
    wrap.appendChild(cnt);
    container.appendChild(wrap);
  }

  function closeAllDropdowns(container, except) {
    var open = container.querySelectorAll('.imh-filter--open');
    for (var i = 0; i < open.length; i++) {
      if (open[i] !== except) open[i].classList.remove('imh-filter--open');
    }
  }

  function getActiveFilters(toolbar) {
    var active = { format: [], place: [], year: [], price: [], orientation: [], availability: false };
    var checks = toolbar.querySelectorAll('input[data-filter]:checked');
    for (var i = 0; i < checks.length; i++) {
      var type = checks[i].getAttribute('data-filter');
      if (type === 'availability') { active.availability = true; continue; }
      if (active[type]) active[type].push(checks[i].value);
    }
    return active;
  }

  function cardMatchesFilters(card, active, priceInfo) {
    // Format (OR)
    if (active.format.length) {
      var dim = (card.getAttribute('data-dim') || '').replace(/\./g, ',');
      if (active.format.indexOf(dim) === -1) return false;
    }
    // Sted (OR)
    if (active.place.length) {
      var tags = (card.getAttribute('data-tags') || '').split(',').map(function(t){return t.trim();});
      var found = false;
      for (var i = 0; i < active.place.length; i++) {
        if (tags.indexOf(active.place[i]) !== -1) { found = true; break; }
      }
      if (!found) return false;
    }
    // Årstall (OR)
    if (active.year.length) {
      var tags2 = (card.getAttribute('data-tags') || '').split(',').map(function(t){return t.trim();});
      var found2 = false;
      for (var i = 0; i < active.year.length; i++) {
        if (tags2.indexOf(active.year[i]) !== -1) { found2 = true; break; }
      }
      if (!found2) return false;
    }
    // Pris (OR mellom grupper)
    if (active.price.length && priceInfo) {
      var p = parseInt(card.getAttribute('data-price')) || 0;
      var inGroup = false;
      for (var i = 0; i < active.price.length; i++) {
        var label = active.price[i];
        if (label.indexOf('Under') === 0 && p < priceInfo.cut1) { inGroup = true; break; }
        if (label.indexOf('Over') === 0 && p > priceInfo.cut2) { inGroup = true; break; }
        if (label.indexOf('–') !== -1 && p >= priceInfo.cut1 && p <= priceInfo.cut2) { inGroup = true; break; }
      }
      if (!inGroup) return false;
    }
    // Orientering (OR)
    if (active.orientation.length) {
      if (active.orientation.indexOf(card._imhOrient) === -1) return false;
    }
    // Tilgjengelighet
    if (active.availability) {
      if (card.getAttribute('data-available') !== 'true') return false;
    }
    return true;
  }

  function applyFilters(grid, allCards, toolbar, priceInfo, L) {
    var active = getActiveFilters(toolbar);
    var visibleCount = 0;
    for (var i = 0; i < allCards.length; i++) {
      if (cardMatchesFilters(allCards[i], active, priceInfo)) {
        allCards[i].classList.remove('imh-card--filtered');
        allCards[i].style.display = '';
        visibleCount++;
      } else {
        allCards[i].classList.add('imh-card--filtered');
        allCards[i].style.display = 'none';
      }
    }
    updateChips(toolbar, active, L);
    grid.dispatchEvent(new Event('imh-relayout'));
  }

  // Kryssfilter-telling: for hver filtertype, tell kort som matcher
  // alle ANDRE aktive filtre (unntatt den aktuelle typen).
  function updateFilterCounts(toolbar, allCards, priceInfo) {
    var active = getActiveFilters(toolbar);
    var filterTypes = ['format', 'place', 'year', 'price', 'orientation'];

    for (var t = 0; t < filterTypes.length; t++) {
      var type = filterTypes[t];
      // Lag en kopi av active der denne typen er tom (ingen filter)
      var crossActive = {};
      for (var k = 0; k < filterTypes.length; k++) {
        crossActive[filterTypes[k]] = (filterTypes[k] === type) ? [] : active[filterTypes[k]];
      }
      crossActive.availability = active.availability;

      // Finn alle kort som matcher kryssfilteret
      var matching = [];
      for (var i = 0; i < allCards.length; i++) {
        if (cardMatchesFilters(allCards[i], crossActive, priceInfo)) {
          matching.push(allCards[i]);
        }
      }

      // Tell per alternativ
      var checkboxes = toolbar.querySelectorAll('input[data-filter="' + type + '"]');
      for (var ci = 0; ci < checkboxes.length; ci++) {
        var val = checkboxes[ci].value;
        var n = 0;

        if (type === 'format') {
          for (var m = 0; m < matching.length; m++) {
            var dim = (matching[m].getAttribute('data-dim') || '').replace(/\./g, ',');
            if (dim === val) n++;
          }
        } else if (type === 'place' || type === 'year') {
          for (var m = 0; m < matching.length; m++) {
            var tags = (matching[m].getAttribute('data-tags') || '').split(',');
            for (var ti = 0; ti < tags.length; ti++) {
              if (tags[ti].trim() === val) { n++; break; }
            }
          }
        } else if (type === 'price' && priceInfo) {
          for (var m = 0; m < matching.length; m++) {
            var p = parseInt(matching[m].getAttribute('data-price')) || 0;
            if (val.indexOf('Under') === 0 && p < priceInfo.cut1) n++;
            else if (val.indexOf('Over') === 0 && p > priceInfo.cut2) n++;
            else if (val.indexOf('–') !== -1 && p >= priceInfo.cut1 && p <= priceInfo.cut2) n++;
          }
        } else if (type === 'orientation') {
          for (var m = 0; m < matching.length; m++) {
            if (matching[m]._imhOrient === val) n++;
          }
        }

        // Oppdater count-span
        var countSpan = checkboxes[ci].parentElement.querySelector('.imh-filter__count');
        if (countSpan) countSpan.textContent = '(' + n + ')';

        // Dim alternativer med 0 treff
        var opt = checkboxes[ci].closest('.imh-filter__opt');
        if (opt) {
          if (n === 0) opt.classList.add('imh-filter__opt--empty');
          else opt.classList.remove('imh-filter__opt--empty');
        }
      }
    }

    // Tilgjengelighets-toggle: tell med alle filtre aktive UNNTATT availability
    var availCb = toolbar.querySelector('input[data-filter="availability"]');
    if (availCb) {
      var crossAvail = {};
      for (var k = 0; k < filterTypes.length; k++) {
        crossAvail[filterTypes[k]] = active[filterTypes[k]];
      }
      crossAvail.availability = false;
      var ac = 0;
      for (var i = 0; i < allCards.length; i++) {
        if (cardMatchesFilters(allCards[i], crossAvail, priceInfo)
            && allCards[i].getAttribute('data-available') === 'true') ac++;
      }
      var acs = availCb.parentElement.querySelector('.imh-filter__count');
      if (acs) acs.textContent = '(' + ac + ')';
    }
  }

  function updateChips(toolbar, active, L) {
    var chipWrap = toolbar.querySelector('[data-imh-chips]');
    if (!chipWrap) return;
    chipWrap.innerHTML = '';
    var anyActive = false;
    var types = ['format', 'place', 'year', 'price', 'orientation'];
    for (var t = 0; t < types.length; t++) {
      var arr = active[types[t]];
      for (var i = 0; i < arr.length; i++) {
        anyActive = true;
        var chip = document.createElement('span');
        chip.className = 'imh-chip';
        chip.textContent = arr[i] + ' ';
        var x = document.createElement('button');
        x.type = 'button';
        x.className = 'imh-chip__x';
        x.textContent = '×';
        x.setAttribute('data-type', types[t]);
        x.setAttribute('data-value', arr[i]);
        chip.appendChild(x);
        chipWrap.appendChild(chip);
      }
    }
    if (active.availability) {
      anyActive = true;
      var chip2 = document.createElement('span');
      chip2.className = 'imh-chip';
      chip2.textContent = L('availability', 'Kun tilgjengelige') + ' ';
      var x2 = document.createElement('button');
      x2.type = 'button';
      x2.className = 'imh-chip__x';
      x2.textContent = '×';
      x2.setAttribute('data-type', 'availability');
      chip2.appendChild(x2);
      chipWrap.appendChild(chip2);
    }
    if (anyActive) {
      var clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'imh-chip imh-chip--clear';
      clear.textContent = L('clear_all', 'Fjern alle');
      clear.setAttribute('data-clear-all', '');
      chipWrap.appendChild(clear);
    }
  }

  function handleClientSort(grid, allCards, sortVal) {
    var items = grid.querySelector('[data-imh-items]');
    grid._imhClientSort = sortVal;
    allCards.sort(function (a, b) {
      var qA = parseInt(a.getAttribute('data-qty')) || 0;
      var qB = parseInt(b.getAttribute('data-qty')) || 0;
      var avA = a.getAttribute('data-available') === 'true' ? 0 : 1;
      var avB = b.getAttribute('data-available') === 'true' ? 0 : 1;
      if (avA !== avB) return avA - avB;
      if (sortVal === 'qty-ascending') return qA - qB;
      return qB - qA;
    });
    for (var i = 0; i < allCards.length; i++) items.appendChild(allCards[i]);
    grid.dispatchEvent(new Event('imh-relayout'));
  }

  // --- Filterbevaring via URL-parametere ---
  function encodeFilters(toolbar) {
    var parts = [];
    var checks = toolbar.querySelectorAll('input[data-filter]:checked');
    for (var i = 0; i < checks.length; i++) {
      var type = checks[i].getAttribute('data-filter');
      var val = checks[i].value || '1';
      parts.push(type + ':' + val);
    }
    return parts.join('|');
  }

  function restoreFilters(toolbar) {
    var params = new URLSearchParams(window.location.search);
    var saved = params.get('imh_f');
    if (!saved) return false;
    var parts = saved.split('|');
    var restored = false;
    for (var i = 0; i < parts.length; i++) {
      var sep = parts[i].indexOf(':');
      if (sep === -1) continue;
      var type = parts[i].substring(0, sep);
      var val = parts[i].substring(sep + 1);
      var sel;
      if (type === 'availability') {
        sel = toolbar.querySelector('input[data-filter="availability"]');
      } else {
        sel = toolbar.querySelector('input[data-filter="' + type + '"][value="' + val + '"]');
      }
      if (sel) { sel.checked = true; restored = true; }
    }
    return restored;
  }

  function initToolbar(grid, toolbar) {
    var itemsWrap = grid.querySelector('[data-imh-items]');
    var allCards = Array.prototype.slice.call(
      itemsWrap.querySelectorAll('[data-imh-card]')
    );
    if (!allCards.length) return;

    // Lagre original DOM-rekkefølge for gjenoppretting
    var originalOrder = allCards.slice();
    grid._imhClientSort = null;

    var cfg = {};
    var keys = ['sort','format','place','year','price','orientation','availability'];
    for (var k = 0; k < keys.length; k++) {
      cfg[keys[k]] = toolbar.getAttribute('data-show-' + keys[k]) !== 'false';
    }

    // Les etiketter fra data-labels (satt av Liquid fra seksjonsinnstillinger)
    var labels = {};
    try { labels = JSON.parse(toolbar.getAttribute('data-labels') || '{}'); } catch(e) {}
    var L = function(key, fallback) { return labels[key] || fallback; };

    var data = scanCards(allCards, L);
    var priceInfo = null;
    var filterRow = toolbar.querySelector('[data-imh-filters]');
    var collUrl = toolbar.getAttribute('data-collection-url') || '';
    var currentSort = toolbar.getAttribute('data-current-sort') || '';

    // Sortering
    var sortSel = null;
    if (cfg.sort) sortSel = buildSortDropdown(filterRow, currentSort, collUrl, L);

    // Filtre
    if (cfg.format) {
      buildDropdown(filterRow, L('format', 'Format'), 'format', data.formats, function (a, b) {
        var na = parseFloat(a.replace(',', '.')) || 0, nb = parseFloat(b.replace(',', '.')) || 0;
        return na - nb;
      });
    }
    if (cfg.place) {
      buildDropdown(filterRow, L('place', 'Sted'), 'place', data.places, function (a, b) {
        return a.localeCompare(b, 'nb');
      });
    }
    if (cfg.year) {
      buildDropdown(filterRow, L('year', 'Årstall'), 'year', data.years, function (a, b) {
        return parseInt(b) - parseInt(a);
      });
    }
    if (cfg.price) {
      var pg = makePriceGroups(data.prices);
      if (pg.groups) {
        priceInfo = pg;
        buildDropdown(filterRow, L('price', 'Pris'), 'price', pg.groups);
      }
    }
    if (cfg.orientation) {
      buildDropdown(filterRow, L('orientation', 'Orientering'), 'orientation', data.orientations);
    }
    if (cfg.availability) {
      var avail = 0;
      for (var i = 0; i < allCards.length; i++) {
        if (allCards[i].getAttribute('data-available') === 'true') avail++;
      }
      if (avail > 0 && avail < allCards.length) buildAvailabilityToggle(filterRow, avail, L);
    }

    // «Ingen treff»-melding
    var noRes = document.createElement('p');
    noRes.className = 'imh-no-results';
    noRes.textContent = L('no_results', 'Ingen arbeider matcher valgte filtre.');
    noRes.style.display = 'none';
    itemsWrap.parentElement.appendChild(noRes);

    // Gjenopprett filtre fra URL (etter serverside-sortering)
    var hadSaved = restoreFilters(toolbar);

    // Filterhendelse — gjenopprett originalrekkefølge først
    function onFilterChange() {
      if (!grid._imhClientSort) {
        for (var i = 0; i < originalOrder.length; i++) {
          itemsWrap.appendChild(originalOrder[i]);
        }
      }
      applyFilters(grid, allCards, toolbar, priceInfo, L);
      updateFilterCounts(toolbar, allCards, priceInfo);
    }

    filterRow.addEventListener('change', function (e) {
      if (e.target.getAttribute('data-imh-sort') !== null) return;
      onFilterChange();
    });

    // Sortering
    if (sortSel) {
      sortSel.addEventListener('change', function () {
        var val = sortSel.value;
        if (!val) return;
        var opt = sortSel.options[sortSel.selectedIndex];
        if (opt.hasAttribute('data-client')) {
          handleClientSort(grid, allCards, val);
        } else {
          // Bevar filtre i URL ved serverside-navigering
          grid._imhClientSort = null;
          var filterStr = encodeFilters(toolbar);
          var url = collUrl + '?sort_by=' + val;
          if (filterStr) url += '&imh_f=' + encodeURIComponent(filterStr);
          window.location.href = url;
        }
      });
    }

    // Chip-klikk
    toolbar.querySelector('[data-imh-chips]').addEventListener('click', function (e) {
      var btn = e.target.closest('[data-type], [data-clear-all]');
      if (!btn) return;
      if (btn.hasAttribute('data-clear-all')) {
        var all = toolbar.querySelectorAll('input[data-filter]:checked');
        for (var i = 0; i < all.length; i++) all[i].checked = false;
      } else {
        var type = btn.getAttribute('data-type');
        var value = btn.getAttribute('data-value');
        var inp = toolbar.querySelector(
          'input[data-filter="' + type + '"]' + (value ? '[value="' + value + '"]' : '')
        );
        if (inp) inp.checked = false;
      }
      onFilterChange();
    });

    // Lukk dropdowns ved klikk utenfor
    document.addEventListener('click', function () {
      closeAllDropdowns(filterRow);
    });

    // Hvis filtre ble gjenopprettet fra URL, aktiver dem nå
    if (hadSaved) onFilterChange();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function () {
    setTimeout(initAll, 50);
  });
})();
